import { DatesSetArg, EventClickArg } from "@fullcalendar/core";
import {
  CHAT_TYPE,
  FEATURES,
  IGroupInfo,
  IScheduledVisits,
  IUserInfo,
} from "@home-health-notify/domain";
import { Divider } from "@mui/material";
import {
  CALENDAR_TABS,
  OrganizationContext,
  useFetchSchedulesVisits,
} from "hhn-commons";
import React, { useContext, useEffect, useState } from "react";
import { ConditionalMapsApiProvider } from "../common/ConditionalMapsApiProvider";
import FullScreenLayout from "../layout/FullScreenLayout";
import {
  CalendarContainer,
  CalendarScheduleWrapper,
  CalendarWrapper,
} from "./Calendar.style";
import CalendarHeader from "./CalendarHeader";
import CalendarMapModal from "./calendarMap/CalendarMapModal";
import { MyCalendar } from "./MyCalendar";
import Schedule from "./Schedule";

interface CalendarProps {
  open?: boolean;
  closeModal: () => void;
  info?: IGroupInfo | IUserInfo;
}

export const Calendar = ({ open, closeModal, info }: CalendarProps) => {
  const { fetchSchedulesVisits, scheduledVisits } = useFetchSchedulesVisits({
    info,
  });

  const [isCalendarMapOpen, setIsCalendarMapOpen] = useState<boolean>(false);
  const [selectedRouteData, setSelectedRouteData] =
    useState<IScheduledVisits[]>();
  const [currentTab, setCurrentTab] = React.useState<CALENDAR_TABS>(
    CALENDAR_TABS.PROJECTED,
  );
  const [dateRange, setDateRange] = useState<{ start: number; end: number }>();
  const [clickedEventId, setClickedEventId] = React.useState<string>();

  const openCalendarMap = (routeData: IScheduledVisits[]) => {
    console.log("openCalendarMap routeData ==>", routeData);
    setSelectedRouteData(routeData);
    setIsCalendarMapOpen(true);
  };

  const closeCalendarMap = () => {
    setIsCalendarMapOpen(false);
  };

  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const { selectedOrgUser } = useContext(OrganizationContext);

  const showCalendar = info ? open : true;
  const isGroupChat = info && info.chatType === CHAT_TYPE.GROUP;
  const isPrivateChat = !!info && info.chatType === CHAT_TYPE.PRIVATE;
  const canViewCalendarMap = isPrivateChat
    ? !!selectedOrgUser?.hasAccess(FEATURES.VIEW_PRIVATE_CALENDAR_MAP)
    : !info
      ? !!selectedOrgUser?.hasAccess(FEATURES.VIEW_CALENDAR_MAP)
      : false;

  const onDateRangeChange = async (datesSet: DatesSetArg) => {
    const startTimestamp = datesSet.start.getTime();
    const endTimestamp = datesSet.end.getTime();

    setDateRange({ start: startTimestamp, end: endTimestamp });
  };

  useEffect(() => {
    if (!!dateRange) {
      fetchSchedulesVisits(dateRange.start, dateRange.end, currentTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, currentTab]);

  const scrollToEvent = (eventId: string) => {
    const listItem = document.getElementById(
      `scheduled-visit-event-${eventId}`,
    );
    if (listItem) {
      listItem.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const onEventClick = (eventData: EventClickArg) => {
    const id = eventData.event.id;
    setClickedEventId(id);
    scrollToEvent(id);
  };

  const showMapForEvents =
    currentTab === CALENDAR_TABS.PROJECTED &&
    !isGroupChat &&
    canViewCalendarMap;

  return (
    showCalendar && (
      <FullScreenLayout>
        <CalendarContainer data-testid="calendar">
          <CalendarHeader
            currentTab={currentTab}
            onTabChange={onTabChange}
            closeModal={closeModal}
            info={info}
          />
          <Divider />
          <CalendarScheduleWrapper>
            <CalendarWrapper>
              <MyCalendar
                onDateRangeChange={onDateRangeChange}
                events={scheduledVisits}
                onEventClick={onEventClick}
                showMapForEvents={showMapForEvents}
                openCalendarMap={openCalendarMap}
                info={isGroupChat ? (info as IGroupInfo) : undefined}
              />
            </CalendarWrapper>
            <Schedule
              events={scheduledVisits}
              clickedEventId={clickedEventId}
            />
          </CalendarScheduleWrapper>
          {isCalendarMapOpen &&
            selectedRouteData &&
            selectedRouteData.length > 0 && (
              <ConditionalMapsApiProvider>
                <CalendarMapModal
                  open={isCalendarMapOpen}
                  closeModal={closeCalendarMap}
                  routeData={selectedRouteData}
                />
              </ConditionalMapsApiProvider>
            )}
        </CalendarContainer>
      </FullScreenLayout>
    )
  );
};
